import { HTMLAttributes, useMemo, useRef } from "react";
import { PlacesType, Tooltip } from "react-tooltip";

import { useTranslationContext } from "context/TranslationContext";
import { useTextClamp } from "hooks/useTextClamp";
import cn from "lib/cn";

import { TAlignText, TWordBreakType } from "constants/index";
import { MAP_TRANSLATION } from "constants/translation";

export interface TruncateTextProps extends HTMLAttributes<HTMLDivElement> {
  text: string;
  id: string;
  placeholder?: string;
  tooltipWidth?: number | string;
  lineHeightTooltip?: number;
  bgColorTooltip?: string;
  withTooltip?: boolean;
  tooltipClassName?: string;
  tooltipPlace?: PlacesType;
  wordBreak?: TWordBreakType;
  textAlign?: TAlignText;
  isEmptyShowDash?: boolean;
  viewMode?: boolean;
}

const TruncateText = ({
  className,
  text,
  placeholder,
  id,
  tooltipWidth,
  lineHeightTooltip,
  bgColorTooltip,
  tooltipClassName,
  withTooltip = true,
  tooltipPlace,
  wordBreak,
  textAlign,
  isEmptyShowDash,
  viewMode,
  ...rest
}: TruncateTextProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslationContext();

  const { isClamped } = useTextClamp(textRef, [id, text]);

  const translatedText = useMemo(() => {
    if (!text) return "";
    return t(MAP_TRANSLATION?.[text] || text);
  }, [text, t]);

  return (
    <div
      id={id}
      ref={textRef}
      data-tooltip-id={id}
      data-tooltip-content={translatedText}
      data-tooltip-hidden={!isClamped || !withTooltip}
      className={cn(["line-clamp-1 break-all", className])}
      {...rest}
    >
      {isEmptyShowDash && !translatedText && "-"}
      {translatedText
        ? translatedText
        : viewMode
          ? "-"
          : placeholder && <div className="text-gray-20">{placeholder}</div>}

      {withTooltip && (
        <Tooltip
          className={cn("z-[999]", tooltipClassName)}
          id={id}
          style={{
            width: tooltipWidth,
            lineHeight: lineHeightTooltip,
            backgroundColor: bgColorTooltip,
            wordBreak: wordBreak,
            textAlign: textAlign,
          }}
          place={tooltipPlace}
        />
      )}
    </div>
  );
};

export default TruncateText;
